import React, { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MapPin,
  CalendarDays,
  IndianRupee,
  Clock,
  ChevronRight,
  ChevronDown,
  Paperclip,
  Users,
  CalendarCheck,
} from "lucide-react";
import ItineraryDetailDialog from "./ItineraryDetailDialog";
import { Icon } from "radix-ui";

// Helpers
function getAttrValue(attributes, keyId) {
  if (!attributes) return undefined;
  const found = attributes.find((a) => a.AttrKeyID === keyId);
  return found?.AttrValue;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/* ────────────────────────────────────────────── */
/* Subcomponents                                */
/* ────────────────────────────────────────────── */

function SearchResultMedia({ title, destinationName, imageUrl, priceFrom }) {
  const hasImage = !!imageUrl;
  const numericPrice = !isNaN(Number(priceFrom)) && Number(priceFrom) > 0 ? Number(priceFrom) : null;

  return (
    <div className="relative md:w-2/5 h-40 md:h-auto">
      {hasImage ? (
        <img
          src={imageUrl}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-muted">
          <div className="text-center px-3">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Yatratickets
            </p>
            <p className="font-semibold text-sm text-foreground">
              {destinationName || "Your next trip"}
            </p>
          </div>
        </div>
      )}

      {destinationName && (
        <div className="absolute top-3 left-3">
          <Badge
            variant="secondary"
            className="bg-background/90 text-foreground border border-border text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
          >
            <MapPin className="h-3 w-3 text-primary" />
            <span className="truncate max-w-[140px] md:max-w-[200px]">
              {destinationName}
            </span>
          </Badge>
        </div>
      )}

      {numericPrice && (
        <div className="absolute bottom-3 right-3">
          <div className="bg-background/90 border border-border px-3 py-1.5 rounded-full shadow-sm flex items-baseline gap-1 text-xs text-foreground">
            <span className="text-[10px] text-muted-foreground mr-1">From</span>
            <span className="font-semibold text-sm">
              NPR {numericPrice.toLocaleString()}
            </span>
            <span className="text-[10px] text-muted-foreground">/ pp</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SearchResultHeader({
  title,
  summary,
  durationText,
  code,
  destinationShort,
  hasFixedDeparture,
  hasGroupPricing
}) {
  return (
    <CardHeader className="p-0 space-y-1">
      <div className="flex justify-between items-start gap-2">
        <CardTitle className="text-base md:text-lg font-semibold leading-snug line-clamp-2">
          {title}
        </CardTitle>
        {/* Pricing Badges */}
        <div className="flex flex-col gap-1 items-end shrink-0">
          {hasFixedDeparture && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 gap-1 border-primary/20 bg-primary/5 text-primary">
              <CalendarCheck className="h-3 w-3" />
              Fixed Departure
            </Badge>
          )}
          {hasGroupPricing && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 gap-1 border-blue-500/20 bg-blue-500/5 text-blue-600">
              <Users className="h-3 w-3" />
              Group Deal
            </Badge>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
        {durationText && (
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="h-3 w-3" />
            {durationText}
          </span>
        )}
        {destinationShort && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {destinationShort}
          </span>
        )}
        {code && (
          <span className="inline-flex items-center gap-1">
            <span className="text-muted-foreground">Code:</span>
            <span className="font-medium text-foreground">{code}</span>
          </span>
        )}
      </div>

      {summary && (
        <CardDescription className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-3 mt-1">
          {summary}
        </CardDescription>
      )}
    </CardHeader>
  );
}

// function SearchResultHighlights({ highlights }) {
//   if (!highlights || highlights.length === 0) return null;

//   const visible = highlights.slice(0, 4);
//   const extra = highlights.length - visible.length;

//   return (
//     <div className="flex flex-wrap gap-1.5">
//       {visible.map((h, idx) => (
//         <Badge
//           key={idx}
//           variant="outline"
//           className="rounded-full text-[11px] font-normal"
//         >
//           {h}
//         </Badge>
//       ))}
//       {extra > 0 && (
//         <span className="text-[11px] text-muted-foreground">
//           +{extra} more
//         </span>
//       )}
//     </div>
//   );
// }

function SearchResultAttributesRow({ hotelCategory, meals, transport }) {
  if (!hotelCategory && !meals && !transport) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
      {hotelCategory && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-foreground">Hotel:</span>
          <span>{hotelCategory}</span>
        </span>
      )}
      {meals && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-foreground">Meals:</span>
          <span>{meals}</span>
        </span>
      )}
      {transport && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-foreground">Transport:</span>
          <span>{transport}</span>
        </span>
      )}
    </div>
  );
}

function SearchResultItineraryPreview({
  itineraries,
  onSelectItinerary,
}) {
  const [showItinerary, setShowItinerary] = useState(false);

  if (!itineraries || itineraries.length === 0) return null;

  const first = itineraries[0];

  const firstPreview = first?.Title
    ? `Day 1: ${first.Title}`
    : "Day wise itinerary available in details.";

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setShowItinerary((prev) => !prev)}
        className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform ${showItinerary ? "rotate-180" : ""
            }`}
        />
        {showItinerary ? "Hide itinerary" : "View itinerary"}
      </button>

      {/* {!showItinerary && (
        <p className="text-[11px] text-muted-foreground">{firstPreview}</p>
      )} */}

      {showItinerary && (
        <ScrollArea className="h-32 pr-2 border border-border/40 rounded-md bg-muted/40">
          <ul className="space-y-1.5 py-2 pr-2">
            {itineraries.map((item, index) => {
              const start = item.TripStartDate
                ? formatDate(item.TripStartDate)
                : "";
              const end = item.TripEndDate
                ? formatDate(item.TripEndDate)
                : "";
              const dateText =
                start || end ? `${start || "N/A"} – ${end || "N/A"}` : "";

              return (
                <li key={item.ItineraryID || index}>
                  <button
                    type="button"
                    onClick={() => onSelectItinerary(item)}
                    className="w-full text-left text-[11px] flex flex-col gap-0.5 rounded-md px-2 py-1 hover:bg-accent/60 transition-colors"
                  >
                    <span className="font-semibold text-foreground line-clamp-1">
                      Day {index + 1}: {item.Title || "Itinerary details"}
                    </span>
                    <span className="text-[11px] text-muted-foreground line-clamp-1">
                      {item.Destination && `${item.Destination} • `}
                      {dateText || "Dates not specified"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────── */
/* Main component                                */
/* ────────────────────────────────────────────── */

// helpers
const stripHtml = (html) =>
  (html || "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();



function SearchResultItem({ item, onOpen }) {
  // ✅ item is: { Package, Destination, Attributes, Itineraries, Files, Prices }
  const pack = item?.Package ?? {};
  const destination = item?.Destination ?? null;
  const attributes = item?.Attributes ?? [];
  const itineraries = item?.Itineraries ?? [];
  const files = item?.Files ?? [];
  const prices = item?.Prices ?? [];

  const highlights = useMemo(() => {
    const text = stripHtml(pack?.Highlights || "");
    if (!text) return [];
    return text
      .split(/[•\n\r|]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(0, 6);
  }, [pack?.Highlights]);

  // Duration shown in search card logic
  const durationText = getAttrValue(attributes, 8);

  const daysAttr = attributes.find(a => a.AttrKey === "Days");
  const finalDurationText = daysAttr ? `${daysAttr.AttrValue} Days` : durationText;

  // Pricing shown in search card logic
  const priceFrom = useMemo(() => {
    if (prices && prices.length > 0) {
      const validPrices = prices
        .map(p => (Number(p.TourFare) || 0) + (Number(p.FlightFare) || 0))
        .filter(f => !isNaN(f) && f > 0);
      if (validPrices.length > 0) {
        return Math.min(...validPrices);
      }
    }
    return getAttrValue(attributes, 7);
  }, [prices, attributes]);

  const hasFixedDeparture = useMemo(() => {
    return prices.some(p => !p.IsGroup && p.FixDepartureSeats > 0);
  }, [prices]);

  const hasGroupPricing = useMemo(() => {
    return prices.some(p => p.IsGroup);
  }, [prices]);

  const hotelCategory = getAttrValue(attributes, 4);
  const transport = getAttrValue(attributes, 5);
  const meals = getAttrValue(attributes, 6);

  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [isItineraryOpen, setIsItineraryOpen] = useState(false);

  const handleSelectItinerary = (itinerary) => {
    setSelectedItinerary(itinerary);
    setIsItineraryOpen(true);
  };

  // ✅ image preference:
  // 1) first file image (Files[0].FileURL)
  // 2) destination.ImageUrl
  const firstFile = files.length > 0 ? files[0] : null;
  const imageUrl =
    firstFile?.FileType === "image"
      ? firstFile?.FileURL
      : destination?.ImageUrl;

  const fileLink = firstFile?.FileURL ? firstFile.FileURL : null;

  return (
    <Card className="group overflow-hidden border border-border bg-background hover:border-primary/60 hover:bg-accent transition-colors duration-150">
      <div id={pack?.PackageID} className="flex flex-col md:flex-row">
        {/* Media */}
        <SearchResultMedia
          title={pack?.Title}
          destinationName={destination?.DestinationName}
          imageUrl={imageUrl}
          priceFrom={priceFrom}
        />

        {/* Content */}
        <div className="flex-1 flex flex-col p-4 md:p-5 gap-3">
          <SearchResultHeader
            title={pack?.Title}
            summary={pack?.Summary}
            durationText={finalDurationText}
            code={pack?.Code}
            destinationShort={destination?.DestinationName}
            hasFixedDeparture={hasFixedDeparture}
            hasGroupPricing={hasGroupPricing}
          />

          {/* <SearchResultHighlights highlights={highlights} /> */}

          <SearchResultAttributesRow
            hotelCategory={hotelCategory}
            meals={meals}
            transport={transport}
          />

          <SearchResultItineraryPreview
            itineraries={itineraries}
            onSelectItinerary={handleSelectItinerary}
          />

          {/* {fileLink && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Paperclip className="h-3 w-3" />
              <a
                href={fileLink}
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary underline-offset-2 hover:underline"
              >
                {firstFile?.FileType || "Download brochure"}
              </a>
            </div>
          )} */}

          <CardFooter className="p-0 mt-auto flex items-center justify-between pt-3">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>
                Instant enquiry on{" "}
                <span className="font-medium text-primary">yatratickets.com</span>
              </span>
            </div>

            <Button size="sm" className="px-4 h-9 text-xs font-medium" onClick={onOpen}>
              View details
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardFooter>
        </div>
      </div>

      <ItineraryDetailDialog
        itinerary={selectedItinerary}
        open={isItineraryOpen}
        onOpenChange={setIsItineraryOpen}
      />
    </Card>
  );
}

export default SearchResultItem;